import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { LifeBuoy, ArrowLeft, Send, Loader2, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FAQ = [
  { q: "Do I need an account?", a: "Yes — a free account lets you make beats, save songs across devices and use the AI. It only takes a moment to sign up." },
  { q: "What's the difference between Free and Pro?", a: "Free gives you the full studio, the AI Song Starter (5 beats/day) and up to 3 saved songs. Pro unlocks unlimited AI beats, unlimited saves, WAV export and the Vocal Remover." },
  { q: "How does the Vocal Remover work?", a: "Upload any song and the AI splits it into vocals, drums, bass, melody and a full instrumental. Load any part onto a deck to flip it. It's a Pro feature." },
  { q: "How do I cancel Pro?", a: "You can cancel anytime from your account — you keep Pro until the end of your billing period. The 7-day trial can be cancelled with no charge." },
];

export default function Support() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.message) {
      toast.error("Please add your email and a message.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/support`, form);
      toast.success("Message sent! We'll get back to you by email.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (_) {
      toast.error("Could not send right now. Please try again shortly.");
    }
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white grain" data-testid="support-page">
      <header className="border-b border-white/5 bg-black/60 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <button data-testid="support-home-btn" onClick={() => navigate("/")} className="flex items-center gap-2 text-neutral-300 hover:text-[#00e5ff] transition-colors">
            <ArrowLeft size={16} /> <span className="font-mono-data text-[11px] uppercase tracking-[0.2em]">Home</span>
          </button>
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="BeatMXR" className="w-7 h-7 rounded-md" />
            <span className="font-display font-black tracking-widest">BEAT<span className="text-[#00e5ff]">MXR</span></span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-12">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#00e5ff]/30 bg-[#00e5ff]/10 px-4 py-1.5 mb-5">
          <LifeBuoy size={13} className="text-[#00e5ff]" />
          <span className="font-mono-data text-[11px] uppercase tracking-[0.28em] text-[#00e5ff]">Support</span>
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl">How can we help?</h1>
        <p className="mt-3 text-neutral-400 max-w-xl">Got a question, a bug, or an idea? Send us a note and we'll get back to you by email.</p>

        <div className="grid md:grid-cols-2 gap-10 mt-10">
          <form onSubmit={submit} className="space-y-4" data-testid="support-form">
            <div className="grid grid-cols-2 gap-4">
              <input data-testid="support-name" value={form.name} onChange={set("name")} placeholder="Name" className="bg-[#111] border border-[#262626] rounded-md px-3 py-2.5 text-sm outline-none focus:border-[#00e5ff]" />
              <input data-testid="support-email" value={form.email} onChange={set("email")} type="email" placeholder="Email *" className="bg-[#111] border border-[#262626] rounded-md px-3 py-2.5 text-sm outline-none focus:border-[#00e5ff]" />
            </div>
            <input data-testid="support-subject" value={form.subject} onChange={set("subject")} placeholder="Subject" className="w-full bg-[#111] border border-[#262626] rounded-md px-3 py-2.5 text-sm outline-none focus:border-[#00e5ff]" />
            <textarea data-testid="support-message" value={form.message} onChange={set("message")} placeholder="Your message *" rows={6} className="w-full bg-[#111] border border-[#262626] rounded-md px-3 py-2.5 text-sm outline-none focus:border-[#00e5ff] resize-none" />
            <button data-testid="support-submit" disabled={sending} className="flex items-center gap-2 rounded-full bg-[#00e5ff] text-black px-6 py-3 text-sm font-bold hover:-translate-y-0.5 transition-transform disabled:opacity-60">
              {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send message
            </button>
            <p className="flex items-center gap-1.5 text-[11px] text-neutral-600"><Mail size={12} /> We reply to the email you provide above.</p>
          </form>

          <div>
            <h2 className="font-display font-black text-xl mb-4">Common questions</h2>
            <div className="space-y-4">
              {FAQ.map((f) => (
                <div key={f.q} className="rounded-lg border border-white/10 bg-[#121214] p-4">
                  <div className="font-semibold text-sm text-neutral-100">{f.q}</div>
                  <p className="text-neutral-400 text-sm mt-1.5 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
